import { isB2B } from '@/helpers/isB2B'
import { formatCurrency } from '@/lib/currency/format-currency'

import type { Currency } from '@/config/tenants/types'
import type { SiteContextModeEnum } from '@/enums/SiteContextModeEnum'
import type { DiscountRowTypeEnumSchema } from '@/schemas/discountRowTypeEnumSchema'
import type { PriceValueSchema } from '@/schemas/priceValueSchema'

export interface SmallestUnitPrice {
  value: number
  unit: string
}

export const getBffPrices = (
  bffPrice: PriceValueSchema | null,
  baseSiteId: SiteContextModeEnum,
  currency: Currency,
  locale: string
) => {
  if (!bffPrice) {
    return null
  }

  const {
    value, //product price
    valueWithVat, //product price with VAT
    fullValue, //product price no discount
    fullValueWithVat, //product price no discount with VAT
    totalValue, // total value for product with quantity
    totalValueWithVat, // total value for product with quantity with VAT
    fullTotalValue, // FULL value, no discount for product with quantity
    fullTotalValueWithVat, // FULL value, no discount for product with quantity with VAT
    discountApplied,
    discountAppliedId,
    priceType,
    discountRowType,
    smallestUnitPrice,
  } = bffPrice

  const activeDiscountApplied = discountApplied != null && discountApplied.length > 0 ? discountApplied[0] : null

  const potentialList: DiscountRowTypeEnumSchema[] = ['CampaignSalesPromotionNode', 'CampaignSalesPromotionPart']

  const potentialSaveCampaigns =
    discountApplied != null
      ? discountApplied?.find(
          (potential) => potential.discountType != null && potentialList.includes(potential.discountType)
        )
      : null

  const percentage: number = potentialSaveCampaigns?.percentage ?? 0

  const getPriceValue = (): number => {
    return isB2B(baseSiteId) ? value : valueWithVat
  }

  const getFullPriceValue = (): number => {
    return isB2B(baseSiteId) ? fullValue : (fullValueWithVat ?? valueWithVat)
  }

  const getTotalPrice = (): number => {
    return isB2B(baseSiteId) ? totalValue : totalValueWithVat
  }

  const getFullTotalPrice = (): number => {
    return isB2B(baseSiteId) ? fullTotalValue : fullTotalValueWithVat
  }

  const getSmallestUnitPrice = (): SmallestUnitPrice | null => {
    if (!smallestUnitPrice) {
      return null
    }

    return {
      value: isB2B(baseSiteId) ? smallestUnitPrice.unitPrice : smallestUnitPrice.unitPriceWithVat,
      unit: smallestUnitPrice.unitName,
    }
  }

  const discountValueApplied = () => {
    if (getFullPriceValue() && getPriceValue() && getFullPriceValue() > getPriceValue()) {
      return getFullPriceValue() - getPriceValue()
    }

    return null
  }

  const discountTotalValueApplied = () => {
    if (getFullTotalPrice() && getTotalPrice() && getFullTotalPrice() > getTotalPrice()) {
      return getFullTotalPrice() - getTotalPrice()
    }

    return null
  }

  const endDateSet = potentialSaveCampaigns?.endDate ?? null
  const minimumApplicableQuantity = potentialSaveCampaigns?.minimumApplicableQuantity ?? null
  const fixSafePrice = activeDiscountApplied?.fixPrice ?? null

  // TODO: Add schema and typings for return object
  return {
    currencyIso: currency,
    formattedValue: formatCurrency(getPriceValue(), currency, locale),
    priceType,
    discountRowType: discountRowType,
    value: getPriceValue(),
    fullValue: getFullPriceValue(),
    totalPrice: getTotalPrice(),
    fullTotalPrice: getFullTotalPrice(),
    discountValue: discountValueApplied(),
    discountTotalValue: discountTotalValueApplied(),
    discountAppliedId: discountAppliedId,
    discountPercentage: percentage,
    quantity: bffPrice.quantity ?? 1,
    endDate: endDateSet,
    minimumQuantity: minimumApplicableQuantity,
    discount: discountValueApplied(),
    fixPrice: fixSafePrice,
    smallestUnitPrice: getSmallestUnitPrice(),
  }
}
